import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { createSaveSelectedVirtualCardIdsAction } from 'src/app/VirtualCardsAllCards/redux/virtualCardsAllCards.actions'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import SvgAlertCircledFilled2 from 'src/components/Svgs/SvgAlertCircledFilled2'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import { CARD_SELECTION_LIMIT } from 'src/components/VirtualCardsBoxAndLinks/useVirtualCardsBulkActions'
import VirtualCardsBoxAndLinks, {
  VirtualCardsAnalyticsEventProperties,
  VirtualCardsListContext,
} from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import VirtualCardsBulkActionsToolbar from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBulkActionsToolbar'
import { VirtualCardTypes } from 'src/enums'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch } from 'src/hooks'
import { BulkAction } from 'src/hooks/bulkActionHooks'
import { useTranslate } from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import { connectedPush } from 'src/navigation/Lnk'
import {
  closeVirtualCardUrl,
  deactivateVirtualCardUrl,
  updateVirtualCardUrl,
} from 'src/navigation/urls'
import { useQueryStatistics } from 'src/queries/statistics/statistics.queries'
import { StatisticsKeys } from 'src/queries/statistics/statistics.queries.constants'
import {
  createSetIsBulkCloseVirtualCardsPopupOpen,
  createSetIsBulkDeactivateVirtualCardsPopupOpen,
  createSetIsBulkUpdateVirtualCardsFlyoutOpen,
} from 'src/redux/actions/app.actions'
import { getVirtualCardPermissionsInBulk } from 'src/redux/actions/data/virtualCards.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import {
  selectSelectedVirtualCardIdsFromState,
  selectVirtualCardPermissionsModelsFromState,
} from 'src/redux/selectors/data/virtualCards.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const VirtualCardsAllCards: React.FC = () => {
  const translate = useTranslate()
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveDirectEnabledCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsFromState,
  )
  const creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount =
    useSelector(
      selectCreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsFromState,
    )
  const creditCardsActiveCanIssueVirtualCardsCount = useSelector(
    selectCreditCardsActiveCanIssueVirtualCardsCountStatisticsFromState,
  )
  const {
    results: statisticsQueries,
    anyLoading: reactQueryStatisticsLoading,
  } = useQueryStatistics({
    statisticsKeys: [
      StatisticsKeys.VirtualCardsActiveCount,
      StatisticsKeys.VirtualCardsCount,
    ],
  })
  const virtualCardsCount = statisticsQueries?.VirtualCardsCount?.data ?? 0
  const appName = useSelector(selectAppName)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const selectedVirtualCardIdsFromRedux = useSelector(
    selectSelectedVirtualCardIdsFromState,
  )
  const virtualCardPermissionsById = useSelector(
    selectVirtualCardPermissionsModelsFromState,
  )
  const { analytics } = useAnalyticsContext()

  const dispatch = useBackgroundDispatch()

  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)

  // TODO: rename these to something more accurate (e.g. bulkUpdateLogic?)
  const [bulkUpdateFlyoutOpen, setBulkUpdateFlyoutOpen] =
    useState<boolean>(false)
  const [bulkDeactivatePopupOpen, setBulkDeactivatePopupOpen] =
    useState<boolean>(false)
  const [bulkClosePopupOpen, setBulkClosePopupOpen] = useState<boolean>(false)
  const [selectedFilters, setSelectedFilters] = useState<
    VirtualCardsAnalyticsEventProperties | undefined
  >(undefined)

  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 150)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 150)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatistics,
      appStatisticsKeys.CreditCardsActiveCanIssueVirtualCardsCountStatistics,
    ])
  }, [dispatchStatistics])

  const readOnly =
    creditCardsActiveCanIssueVirtualCardsCount === 0 ||
    creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCount === 0 ||
    creditCardsActiveDirectEnabledCanIssueVirtualCardsCount === 0

  const bulkVcnSelectedAnalyticsEvent = (totalSelectedCards: number): void => {
    analytics?.track('virtual cards - all cards - bulk VCN selected', {
      number_of_cards: totalSelectedCards,
    })
  }

  const filteredVirtualCardsPermissions = useMemo(
    () =>
      (selectedVirtualCardIdsFromRedux ?? []).map((virtualCardId) => ({
        ...virtualCardPermissionsById[virtualCardId],
        virtualCardId,
      })),
    [selectedVirtualCardIdsFromRedux, virtualCardPermissionsById],
  )

  const showBulkSelectionLimitToast = (): void => {
    dispatch(
      createSetIsToastOpen(true, {
        toastMessage: translate(
          'You can only bulk update ${num} virtual cards at a time',
          {
            num: CARD_SELECTION_LIMIT.toString(),
          },
        ),
        partialSuccessColorScheme: true,
        image: (
          <SvgAlertCircledFilled2
            css={css`
              transform: scale(0.8);
              margin-left: -10px;
            `}
          />
        ),
      }),
    )
  }

  const trackBulkAction = (
    bulkAction: BulkAction | undefined,
    selectedVirtualCards: VirtualCardListModel[],
  ): void => {
    const cardTypeArray: VirtualCardTypes[] = []

    selectedVirtualCards.forEach((vc) => {
      if (vc.recurs) {
        if (vc.direct) {
          cardTypeArray.push(VirtualCardTypes.recurringGuest)
        } else {
          cardTypeArray.push(VirtualCardTypes.recurring)
        }
      } else if (vc.direct) {
        cardTypeArray.push(VirtualCardTypes.guestAccess)
      } else {
        cardTypeArray.push(VirtualCardTypes.standard)
      }
    })

    analytics?.track(`bulk update vcn - ${bulkAction}`, {
      card_type: cardTypeArray.filter(
        (value, index, array) => array.indexOf(value) === index,
      ),
      number_of_cards: selectedVirtualCards.length,
    })
  }

  // bulk update/deactivate virtual cards flyout/popup open
  useEffect(() => {
    // check if permissions are fully loaded in
    if (
      filteredVirtualCardsPermissions.filter(
        (permission) => permission?.permissions === undefined,
      ).length === 0
    ) {
      const cardsThatCanBeUpdated = filteredVirtualCardsPermissions.filter(
        (permission) =>
          permission?.permissions?.includes(
            VirtualCardPermissions.UpdateVirtualCard,
          ),
      )
      const cardsThatCantBeUpdated = filteredVirtualCardsPermissions.filter(
        (permission) =>
          !permission?.permissions?.includes(
            VirtualCardPermissions.UpdateVirtualCard,
          ),
      )

      if (
        (selectedVirtualCardIdsFromRedux?.length || 0) > 0 &&
        cardsThatCantBeUpdated.length === filteredVirtualCardsPermissions.length
      ) {
        // display can't update toast
        dispatch(
          createSetIsToastOpen(true, {
            toastMessage: translate(
              'Oops! You can only edit virtual cards you have created or have access to as a budget user',
            ),
            failureColorScheme: true,
            hideImage: true,
          }),
        )
      } else {
        if (bulkUpdateFlyoutOpen) {
          if (cardsThatCanBeUpdated.length === 1) {
            dispatch(
              connectedPush(
                updateVirtualCardUrl(cardsThatCanBeUpdated[0].virtualCardId),
              ),
            )
          } else {
            dispatch(createSetIsBulkUpdateVirtualCardsFlyoutOpen(true))
          }
          setBulkUpdateFlyoutOpen(false)
        }
        if (bulkDeactivatePopupOpen) {
          if (
            cardsThatCanBeUpdated.length === 1 &&
            selectedVirtualCardIdsFromRedux?.length === 1
          ) {
            dispatch(
              connectedPush(
                deactivateVirtualCardUrl(
                  cardsThatCanBeUpdated[0].virtualCardId,
                ),
              ),
            )
          } else {
            dispatch(createSetIsBulkDeactivateVirtualCardsPopupOpen(true))
          }
          setBulkDeactivatePopupOpen(false)
        }
        if (bulkClosePopupOpen) {
          if (
            cardsThatCanBeUpdated.length === 1 &&
            selectedVirtualCardIdsFromRedux?.length === 1
          ) {
            dispatch(
              connectedPush(
                closeVirtualCardUrl(cardsThatCanBeUpdated[0].virtualCardId),
              ),
            )
          } else {
            dispatch(createSetIsBulkCloseVirtualCardsPopupOpen(true))
          }
          setBulkClosePopupOpen(false)
        }
      }
    }
  }, [
    dispatch,
    filteredVirtualCardsPermissions,
    selectedVirtualCardIdsFromRedux?.length,
    bulkUpdateFlyoutOpen,
    bulkDeactivatePopupOpen,
    bulkClosePopupOpen,
  ])

  // analytics
  useEffect(() => {
    if (selectedFilters) {
      analytics?.track('all cards filter applied', {
        card_user:
          selectedFilters.card_user && selectedFilters.card_user.length > 0,
        card_type:
          selectedFilters.card_type && selectedFilters.card_type.length > 0,
        status: selectedFilters.status && selectedFilters.status.length > 0,
        search: !!selectedFilters.search,
        deactivating_range: !!selectedFilters.deactivating_in,
      })
    }
  }, [selectedFilters])

  return !(
    statisticsLoaded.creditCardsActiveDirectEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveRecurrenceEnabledCanIssueVirtualCardsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveCanIssueVirtualCardsCountStatisticsLoaded
  ) || reactQueryStatisticsLoading ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`All Virtual Cards | ${appName}`}</title>
        <meta
          name="description"
          content={`All Virtual Cards page of ${appName}`}
        />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent noStackingIndex noPadding>
          <VirtualCardsBoxAndLinks
            context={VirtualCardsListContext.AllCards}
            emptyStateDesign={TableEmptyStateDesigns.AccountVirtualCardsActive}
            totalCount={virtualCardsCount}
            readOnly={readOnly}
            showBulkActions
            selectedVirtualCardIds={selectedVirtualCardIdsFromRedux}
            setSelectedVirtualCardIds={(ids) =>
              dispatch(createSaveSelectedVirtualCardIdsAction(ids))
            }
            onBulkSelectionLimit={showBulkSelectionLimitToast}
            onBulkSelectionAnalytics={bulkVcnSelectedAnalyticsEvent}
            onBulkAction={(selectedIds) => {
              dispatch(getVirtualCardPermissionsInBulk(selectedIds))
            }}
            onBulkActionAnalytics={trackBulkAction}
            renderBulkActionsToolbar={({
              selectedVirtualCards,
              clearSelection,
              handleBulkAction,
            }) => (
              <VirtualCardsBulkActionsToolbar
                selectedVirtualCards={selectedVirtualCards}
                onUpdate={() => {
                  handleBulkAction(BulkAction.Update)
                  setBulkUpdateFlyoutOpen(true)
                }}
                onDeactivate={() => {
                  handleBulkAction(BulkAction.Deactivate)
                  setBulkDeactivatePopupOpen(true)
                }}
                onClose={() => {
                  handleBulkAction(BulkAction.CloseCards)
                  setBulkClosePopupOpen(true)
                }}
                onClearSelection={clearSelection}
                locale={locale}
              />
            )}
            setSelectedFilters={setSelectedFilters}
            clientHeight={clientHeight || 0}
          />
        </BackgroundPageContent>
      </div>
    </>
  )
}

export default VirtualCardsAllCards
